import { Suspense } from 'react';

import { TAccount } from '@helpers/Accounts/accounts.types';
import { DEOnly, UKOnly } from '@iwIntlProvider/CountryWrappers';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconQuestionMarkOutline } from '@iwoca/icons';
import { Accordion } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { SidebarSectionTitle } from '../SidebarSectionTitle';
import { FAQContentDE } from './FAQContentDE';
import styles from './FAQs.module.css';
import { RBLFAQs } from './RBLFAQs';
import { RCFFAQs } from './RCFFAQs';
import { useGetFAQData } from './useGetFAQData';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';

export const FAQs = ({ accountId }: { accountId: string }) => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <div
                        className="col-span-2"
                        data-testId="loan-overview-loading"
                    >
                        <MainContentPending />
                    </div>
                }
            >
                <div className="col-span-2 row-span-6 max-sm:order-last max-sm:col-span-full">
                    <FAQContent accountId={accountId} />
                </div>
            </Suspense>
        </ErrorBoundary>
    );
};

const FAQContent = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => {
    const {
        loanDuration,
        isOverdue,
        topUpStatus,
        isRBL,
        isInFirstRepaymentTopUpSplitTest,
        isRCF
    } = useGetFAQData(accountId);

    const isTopUpApproved = topUpStatus === 'TOPUP_APPROVED';
    const isTopUpEligible = topUpStatus === 'TOPUP_ELIGIBLE';

    const trackFaqClicked = (faqTitle: string) => {
        tracking.ampli.faqClicked({
            account_id: accountId,
            faq_title: faqTitle
        });
    };

    if (isRBL) {
        return (
            <DEOnly>
                <SidebarSectionTitle>
                    <FormattedMessage
                        id="_.faq_widget.title"
                        defaultMessage="Frequently asked questions"
                    />
                </SidebarSectionTitle>
                <RBLFAQs trackFaqClicked={trackFaqClicked} />
            </DEOnly>
        );
    }

    if (isRCF) {
        return (
            <div>
                <SidebarSectionTitle>
                    <FormattedMessage
                        id="_.faq_widget.title"
                        defaultMessage="Frequently asked questions"
                    />
                </SidebarSectionTitle>
                <RCFFAQs trackFaqClicked={trackFaqClicked} />
            </div>
        );
    }

    return (
        <>
            <DEOnly>
                <SidebarSectionTitle>
                    <FormattedMessage
                        id="_.faq_widget.title"
                        defaultMessage="Frequently asked questions"
                    />
                </SidebarSectionTitle>
                <FAQContentDE
                    trackFaqClicked={trackFaqClicked}
                    loanDuration={loanDuration}
                />
            </DEOnly>
            <UKOnly>
                <>
                    <SidebarSectionTitle>
                        <FormattedMessage
                            id="_.faq_widget.title"
                            defaultMessage="Frequently asked questions"
                        />
                    </SidebarSectionTitle>
                    <div className="flex flex-col gap-s">
                        {!isOverdue && (
                            <>
                                {!isTopUpApproved ? (
                                    <Accordion
                                        frame
                                        icon={<IconQuestionMarkOutline />}
                                        title="Can I request more funds?"
                                        small
                                        data-testid="can-i-request-more-funds"
                                        onClick={() =>
                                            trackFaqClicked(
                                                'Can I request more funds?'
                                            )
                                        }
                                    >
                                        <CanIRequestMoreFundsAnswer
                                            loanDuration={loanDuration}
                                            isTopUpEligible={isTopUpEligible}
                                            isInFirstRepaymentTopUpSplitTest={
                                                isInFirstRepaymentTopUpSplitTest
                                            }
                                        />

                                        <br />
                                        <a
                                            href="https://support.iwoca.co.uk/en/articles/498744-can-i-top-up-my-loan"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.link}
                                        >
                                            Read more
                                        </a>
                                    </Accordion>
                                ) : (
                                    <>
                                        <Accordion
                                            frame
                                            icon={<IconQuestionMarkOutline />}
                                            title="How many times can I withdraw funds?"
                                            small
                                            onClick={() =>
                                                trackFaqClicked(
                                                    'How many times can I withdraw funds?'
                                                )
                                            }
                                        >
                                            You can withdraw funds as many times
                                            as you'd like, provided your
                                            approval window is still active. You
                                            can see how long you have left at
                                            the top of this page.
                                        </Accordion>

                                        <Accordion
                                            frame
                                            icon={<IconQuestionMarkOutline />}
                                            title="How much can I withdraw at once?"
                                            small
                                            onClick={() =>
                                                trackFaqClicked(
                                                    'How much can I withdraw at once?'
                                                )
                                            }
                                        >
                                            You can withdraw any amount above
                                            £100, until you reach your credit
                                            limit.
                                        </Accordion>
                                    </>
                                )}

                                <Accordion
                                    frame
                                    icon={<IconQuestionMarkOutline />}
                                    title="How do early repayments work?"
                                    small
                                    data-testid="how-do-early-repayments-work"
                                    onClick={() =>
                                        trackFaqClicked(
                                            'How do early repayments work?'
                                        )
                                    }
                                >
                                    <p className={styles.faqText}>
                                        If you repay more than your agreed
                                        monthly repayment, you’re technically
                                        repaying early –{' '}
                                        <strong>there’s no fee for that</strong>
                                        .
                                    </p>
                                    <br />
                                    <p className={styles.faqText}>
                                        You can do it as a one-off via instant
                                        bank transfer, your linked debit card,
                                        or manual transfer.
                                    </p>
                                    <br />
                                    <p className={styles.faqText}>
                                        When you do, you can either pay off your
                                        full loan there and then, or give
                                        yourself a{' '}
                                        <strong>repayment break</strong> - e.g.
                                        if your monthly payment is £1,000 a
                                        month, and you repay £3,000 one month,
                                        you’ll have two months off. (You’ll
                                        still accrue interest for this time,
                                        though.)
                                    </p>
                                    <br />
                                    <a
                                        href="https://support.iwoca.co.uk/en/articles/592981-how-do-repayments-work"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.link}
                                    >
                                        Read more
                                    </a>
                                </Accordion>
                            </>
                        )}

                        <Accordion
                            frame
                            icon={<IconQuestionMarkOutline />}
                            title="What if I can't repay?"
                            small
                            data-testid="what-if-i-cant-repay"
                            onClick={() =>
                                trackFaqClicked("What if I can't repay?")
                            }
                        >
                            <p className={styles.faqText}>
                                If you have trouble repaying, let us know so we
                                can work with you to find a solution. We might
                                be able to lower your monthly repayments, or
                                move the date you pay them.
                            </p>
                            <br />
                            <p className={styles.faqText}>
                                There are lots of different ways we can help, so
                                please call us on{' '}
                                <a href="tel:020 3397 3375">
                                    <strong>020 3397 3375</strong>
                                </a>
                                .
                            </p>
                            <br />
                            <a
                                href="https://support.iwoca.co.uk/en/articles/1608757-how-we-can-help-if-you-miss-a-payment-or-you-re-struggling-to-repay"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.link}
                            >
                                Read more
                            </a>
                        </Accordion>
                    </div>
                </>
            </UKOnly>
        </>
    );
};

const CanIRequestMoreFundsAnswer = ({
    loanDuration,
    isTopUpEligible,
    isInFirstRepaymentTopUpSplitTest
}: {
    loanDuration: number;
    isTopUpEligible: boolean;
    isInFirstRepaymentTopUpSplitTest: boolean;
}) => {
    if (!isInFirstRepaymentTopUpSplitTest) {
        return (
            <>
                <p className={styles.faqText}>
                    You can make a request once your{' '}
                    <strong>
                        principal is below two thirds of your credit limit{' '}
                    </strong>
                    - e.g. £20k if your limit is £30k.
                </p>
                <br />
                <p className={styles.faqText}>
                    You can request a new credit limit{' '}
                    <strong>up to £1m</strong>, and we’ll give you a decision
                    within <strong>1 business day</strong> based on updated info
                    like bank statements. If you’re requesting up to £15k we’ll
                    give you an <strong>instant decision</strong>.
                </p>
                <br />
                <p className={styles.faqText}>
                    When you take more funds, you’ll have{' '}
                    <strong>another {loanDuration}</strong> months to pay off
                    the loan.
                </p>
            </>
        );
    }
    if (isTopUpEligible) {
        return (
            <>
                <p className={styles.faqText}>
                    Good news: You are now eligible to request more funds.
                </p>
                <br />
                <p className={styles.faqText}>
                    You can apply for a limit <strong>up to £1m</strong>, or get
                    an <strong>instant decision</strong> on up to £15k. Any
                    top-up will reset your repayment term to a full{' '}
                    {loanDuration} months.
                </p>
            </>
        );
    }
    return (
        <>
            <p className={styles.faqText}>
                You're almost there. You'll be eligible to request more funds as
                soon as your{' '}
                <strong>first scheduled repayment is processed</strong> (usually
                30 days after your loan started).
            </p>
            <br />
            <p className={styles.faqText}>
                Check back then to request a new credit limit{' '}
                <strong>up to £1m</strong>, or get an{' '}
                <strong>instant decision</strong> on up to £15k.
            </p>
        </>
    );
};
